import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEntitlements } from "../hooks/useEntitlement";
import {
  COBRANZA_ENTITLEMENT,
  cobranzaSwitchedOff,
  useIsCobranzaOperator,
  useIsFinanceOnly,
} from "../hooks/useIsCobranzaOperator";

/**
 * Secciones de Cobranza que NO son para el rol finance: son las pantallas
 * generales de usuarios y dispositivos (alta, edición y baja), de plataforma.
 */
export const PLATFORM_ONLY_COBRANZA_SECTIONS: readonly string[] = ["usuarios", "dispositivos"];

/** Sección de Cobranza de una ruta, en minúsculas (el router no distingue mayúsculas). */
export function cobranzaSectionOf(pathname: string): string {
  return pathname.replace(/^\/cobranza\/?/i, "").split("/")[0].toLowerCase();
}

/**
 * Guardia de la app Cobranza (#1264), con la MISMA regla que la API
 * (`assertCobranzaOperator`):
 *
 *  - administrador de plataforma u owner → como siempre, por sus derechos
 *    (`app:cobranza`): licencia, rol y bandera siguen pudiendo cerrarla;
 *  - rol finance → por su rol. La app nace `internal` y los derechos lo dejarían
 *    fuera, así que no se le consultan… salvo la bandera global APAGADA, que es
 *    el interruptor de emergencia y vale para todos.
 *
 * Por eso `/cobranza` está fuera de las guardias automáticas del catálogo
 * (entitlementGates): la decide esta guardia.
 */
export default function CobranzaOperatorRoute({ redirectTo = "/apps" }: { redirectTo?: string }) {
  const isOperator = useIsCobranzaOperator();
  const financeOnly = useIsFinanceOnly();
  const { can, reasonFor } = useEntitlements();
  const location = useLocation();

  if (!isOperator) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ deniedPath: location.pathname, reason: "cobranza_operator_required" }}
      />
    );
  }

  const reason = reasonFor(COBRANZA_ENTITLEMENT);
  const denied = financeOnly ? cobranzaSwitchedOff(reason) : !can(COBRANZA_ENTITLEMENT);
  if (denied) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ deniedPath: location.pathname, reason, entitlement: COBRANZA_ENTITLEMENT }}
      />
    );
  }

  if (financeOnly && PLATFORM_ONLY_COBRANZA_SECTIONS.includes(cobranzaSectionOf(location.pathname))) {
    return <Navigate to="/cobranza/resumen" replace />;
  }

  return <Outlet />;
}
